import { useCallback, useEffect, useRef } from 'react'

import { GroupMessageType } from '@/entities/feedbacks'
import { HidingSpin } from '@/shared/ui'
import { Card, Divider, Flex, Image, List, Typography } from 'antd'
import clsx from 'clsx'
import dayjs from 'dayjs'
import { useGate, useUnit } from 'effector-react'
import VirtualList, { type ListRef } from 'rc-virtual-list'

import { getItemKey, isLastMessageInGroup } from '../../lib/chat-helpers'
import {
  $isChatTouchedByUser,
  $loadingChatInitially,
  $messagesWithSeparators,
  FeedbackChatGate,
  chatScrolledToEnd,
  feedbackChatRequestedNextPage,
  markMessageAsVisibleRequested,
} from '../../model/feedback-chat.model'
import styles from './styles.module.scss'

interface FeedbackChatMessageListProps {
  chatId: string
  listContainerRef: React.RefObject<HTMLDivElement | null>
  containerHeight: number
}

export function FeedbackChatMessageList({
  chatId,
  listContainerRef,
  containerHeight,
}: FeedbackChatMessageListProps) {
  const messagesWithSeparators = useUnit($messagesWithSeparators)
  const handlePageChange = useUnit(feedbackChatRequestedNextPage)
  const handleChatScrolledToEnd = useUnit(chatScrolledToEnd)
  const isChatTouchedByUser = useUnit($isChatTouchedByUser)
  const loadingChatInitially = useUnit($loadingChatInitially)
  const requestMarkMessageAsVisible = useUnit(markMessageAsVisibleRequested)

  //   Refs
  const virtualListRef = useRef<ListRef>(null)
  const scrollIsAtBottomRef = useRef(false)

  // Callbacks
  const scrollHeightBeforeLoadingOldMessagesRef = useRef(0)
  const onOldMessageRecieved = useCallback(() => {
    requestAnimationFrame(() => {
      if (!virtualListRef.current) return

      const innerList = virtualListRef.current.nativeElement.querySelector(
        '.rc-virtual-list-holder-inner',
      )
      if (innerList === null) return
      const oldScrollHeight = scrollHeightBeforeLoadingOldMessagesRef.current
      const newScrollHeight = innerList.scrollHeight
      const scrollDiff = newScrollHeight - oldScrollHeight
      const previousScrollTop = virtualListRef.current.getScrollInfo().y
      virtualListRef.current.scrollTo({
        top: previousScrollTop + scrollDiff,
      })
      scrollHeightBeforeLoadingOldMessagesRef.current = newScrollHeight
    })
  }, [])

  const messagesWithSeparatorsRef = useRef(messagesWithSeparators)
  messagesWithSeparatorsRef.current = messagesWithSeparators
  const onOldUnreadMessageRecieved = useCallback(() => {
    if (!virtualListRef.current) return
    const existUnread = messagesWithSeparatorsRef.current.some(
      (entry) => entry.type === GroupMessageType.UNREAD,
    )
    if (existUnread) {
      virtualListRef.current.scrollTo({
        key: GroupMessageType.UNREAD,
        align: 'top',
      })
    }
  }, [])

  const onNewMessageRecieved = useCallback(() => {
    if (!virtualListRef.current) return
    if (scrollIsAtBottomRef.current) {
      virtualListRef.current.scrollTo({
        index: messagesWithSeparatorsRef.current.length - 1,
        align: 'top',
      })
    }
  }, [])

  // Read helper

  const getLastVisibleUnreadMessageId = useCallback((): string | null => {
    if (!listContainerRef.current) return null
    const containerRect = listContainerRef.current.getBoundingClientRect()
    const messageElements =
      listContainerRef.current.querySelectorAll('[data-key]')
    let candidate: string | null = null
    messageElements.forEach((el) => {
      const rect = el.getBoundingClientRect()
      const isFullyVisible =
        rect.top >= containerRect.top && rect.bottom <= containerRect.bottom
      if (isFullyVisible) {
        const id = el.getAttribute('data-key')
        const isRead = el.getAttribute('data-read') === 'true'
        if (id && !isRead) candidate = id
      }
    })
    return candidate
  }, [listContainerRef])

  const tryReadLeastVisibleMessage = useCallback(() => {
    const lastVisibleUnreadMessageId = getLastVisibleUnreadMessageId()
    if (lastVisibleUnreadMessageId) {
      requestMarkMessageAsVisible(lastVisibleUnreadMessageId)
    }
  }, [getLastVisibleUnreadMessageId, requestMarkMessageAsVisible])

  useGate(FeedbackChatGate, {
    chatId,
    onNewMessageRecieved,
    onOldMessageRecieved,
    onOldUnreadMessageRecieved,
  })

  // Handlers
  const handleScroll = (e: React.UIEvent<HTMLElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (scrollTop <= 200) {
      handlePageChange()
      return
    }
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10
    if (isAtBottom) {
      handleChatScrolledToEnd()
    }
    scrollIsAtBottomRef.current = isAtBottom
    if (isChatTouchedByUser) {
      tryReadLeastVisibleMessage()
    }
  }

  useEffect(() => {
    if (isChatTouchedByUser) {
      tryReadLeastVisibleMessage()
    }
  }, [isChatTouchedByUser, tryReadLeastVisibleMessage])

  return (
    <List ref={listContainerRef} className={styles.listContainer} split={false}>
      <HidingSpin spinning={loadingChatInitially}>
        <VirtualList
          ref={virtualListRef}
          data={messagesWithSeparators}
          height={containerHeight}
          onScroll={handleScroll}
          itemKey={getItemKey}
        >
          {(entry, index) => (
            <List.Item
              className={clsx(
                styles.messageContainer,
                entry.type === GroupMessageType.USER_MESSAGE &&
                  entry.item.isCurrentUserMessage &&
                  styles.messageContainerCurrentUser,
              )}
            >
              {entry.type === GroupMessageType.DATE ? (
                <Divider plain className={styles.specialMessage}>
                  {entry.date}
                </Divider>
              ) : entry.type === GroupMessageType.UNREAD ? (
                <Divider plain className={styles.specialMessage}>
                  Непрочитанные сообщения
                </Divider>
              ) : entry.type === GroupMessageType.SYSTEM_MESSAGE ? (
                <Divider
                  data-key={entry.item.id}
                  plain
                  className={styles.specialMessage}
                >
                  {entry.item.text}
                </Divider>
              ) : entry.type === GroupMessageType.USER_MESSAGE ? (
                <>
                  {(() => {
                    const lastInGroup = isLastMessageInGroup(
                      messagesWithSeparators,
                      index,
                      entry,
                    )
                    const currentTime = dayjs(entry.item.date).format('HH:mm')
                    return (
                      <Flex vertical className={styles.messageContent} gap={8}>
                        <Flex data-key={entry.item.id} align="end">
                          {!entry.item.attachment ? (
                            <Card
                              className={clsx(
                                styles.messageCard,
                                lastInGroup && styles.messageCardLastInGroup,
                              )}
                              classNames={{ body: styles.messageCardBody }}
                            >
                              <Typography.Text className={styles.messageText}>
                                {entry.item.text}
                              </Typography.Text>
                            </Card>
                          ) : (
                            <Card
                              className={clsx(
                                styles.messageCard,
                                lastInGroup && styles.messageCardLastInGroup,
                              )}
                              classNames={{ body: styles.imageCardBody }}
                              styles={{
                                body: {
                                  width: 200,
                                  height:
                                    200 /
                                    (entry.item.attachment.aspectRatio ?? 1),
                                },
                              }}
                            >
                              <Image src={entry.item.attachment?.fullS3Url} />
                            </Card>
                          )}
                        </Flex>

                        {lastInGroup ? (
                          <Flex
                            justify={
                              entry.item.isCurrentUserMessage ? 'end' : 'start'
                            }
                            align="center"
                            style={{ width: '100%' }}
                            gap={8}
                          >
                            <Typography.Text type="secondary">
                              {currentTime}
                            </Typography.Text>
                          </Flex>
                        ) : null}
                      </Flex>
                    )
                  })()}
                </>
              ) : null}
            </List.Item>
          )}
        </VirtualList>
      </HidingSpin>
    </List>
  )
}
